import { Injectable, inject } from '@angular/core';
import { Observable, Subject, map, of, share, tap } from 'rxjs';

import { DashboardConfigurationModel } from '../models/dashboard-configuration.model';
import { LayoutSettingsModel } from '../models/layout-settings.model';
import { CacheService } from './cache.service';
import { UsersResource } from './resources/users.resource';

@Injectable({
    providedIn: 'root'
})
export class LayoutSettingsService {
    readonly onSidebarCollapsedChange: Observable<boolean>;

    private currentRequest?: Observable<LayoutSettingsModel>;

    private readonly onSidebarCollapsedChangeSub = new Subject<boolean>();
    private readonly cacheKey = 'layout';
    private readonly cacheService = new CacheService();
    private readonly usersResource = inject(UsersResource);

    constructor() {
        this.onSidebarCollapsedChange = this.onSidebarCollapsedChangeSub.asObservable();
    }

    getLayoutSettings(): Observable<LayoutSettingsModel> {
        const value = this.cacheService.get<LayoutSettingsModel>(this.cacheKey);
        if (!!value) {
            return of(value);
        }

        if (!this.currentRequest) {
            this.currentRequest = this.usersResource.getLayoutSettings().pipe(
                map(data => data ?? new LayoutSettingsModel()),
                tap(data => {
                    this.cacheService.set(this.cacheKey, data);
                    this.currentRequest = undefined;
                }),
                share()
            );
        }

        return this.currentRequest;
    }

    setNavigationCollapsed(isCollapsed: boolean): void {
        this.onSidebarCollapsedChangeSub.next(isCollapsed);
        this.getLayoutSettings().subscribe(settings => {
            this.updateLayoutSettings({ ...settings, isNavigationCollapsed: isCollapsed });
        });
    }

    updateDashboard(dashboardConfig: DashboardConfigurationModel): void {
        this.getLayoutSettings().subscribe(settings => {
            this.updateLayoutSettings({ ...settings, dashboard: dashboardConfig });
        });
    }

    resetLayoutSettings(): Observable<any> {
        this.cacheService.clear(this.cacheKey);
        return this.usersResource.removeLayoutSettings();
    }

    private updateLayoutSettings(newSettings: LayoutSettingsModel): void {
        this.cacheService.clear(this.cacheKey);
        this.usersResource.saveLayoutSettings(newSettings).subscribe();
    }
}
